import React, { useCallback, useEffect, useRef, useState } from "react";
import { JitsiMeeting, JaaSMeeting } from "@jitsi/react-sdk";
import bizLogo from "../assets/biz-consultancy-logo.png";
import {
  JITSI_MODE,
  JITSI_DOMAIN,
  JAAS_APP_ID,
  getJitsiConfigOverwrite,
  getJitsiInterfaceConfig,
  fetchJaasToken,
} from "../config/jitsi";
import { AudioRecoveryBanner } from "./AudioRecoveryBanner";
import { RecordingPanel } from "./RecordingPanel";
import { useMeetingAudioRecovery } from "../meeting/useMeetingAudioRecovery";
import { createMeetingLogger } from "../meeting/meetingLog";

function applyIframeStyle(iframeRef) {
  if (!iframeRef) return;
  iframeRef.style.height = "100%";
  iframeRef.style.width = "100%";
  iframeRef.style.border = "0";
  iframeRef.style.borderRadius = "0";
  iframeRef.allow =
    "camera; microphone; display-capture; fullscreen; autoplay; clipboard-write";
}

function MeetingChrome({ children, roomName }) {
  return (
    <div className="fixed inset-0 flex flex-col bg-biz-deep">
      <div className="pointer-events-none absolute left-3 top-3 z-50 flex items-center gap-2 rounded-xl bg-biz-deep/70 px-2.5 py-1.5 shadow-soft ring-1 ring-white/10 backdrop-blur-md sm:left-4 sm:top-4">
        <img
          src={bizLogo}
          alt="BIZ Consultancy"
          className="h-8 w-8 rounded-lg object-cover"
        />
        <div className="hidden pr-1 sm:block">
          <p className="text-[11px] font-semibold leading-tight text-white">
            BIZ Meeting
          </p>
          {roomName ? (
            <p className="max-w-[140px] truncate text-[10px] text-white/55">
              {roomName}
            </p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}

export function JitsiMeetingView({
  roomName,
  participantName,
  asHost = false,
  onLeave,
  onRejoin,
}) {
  const apiRef = useRef(null);
  const recoveryRef = useRef(null);
  const logMeeting = useRef(createMeetingLogger(roomName)).current;
  const [externalApi, setExternalApi] = useState(null);
  const [jwt, setJwt] = useState(null);
  const [appId, setAppId] = useState(JAAS_APP_ID);
  const [loading, setLoading] = useState(JITSI_MODE === "jaas");
  const [error, setError] = useState("");
  const recovery = useMeetingAudioRecovery({
    jitsiDomain: JITSI_DOMAIN,
    onRejoin,
  });
  recoveryRef.current = recovery;

  const handleApiReady = useCallback(
    (externalApi) => {
      apiRef.current = externalApi;
      setExternalApi(externalApi);
      externalApi.addListener("videoConferenceLeft", () => onLeave());
      externalApi.addListener("readyToClose", () => onLeave());
      if (recoveryRef.current) recoveryRef.current.attach(externalApi);
    },
    [onLeave]
  );

  useEffect(() => {
    if (JITSI_MODE !== "jaas") return;

    let cancelled = false;
    setLoading(true);
    setError("");

    fetchJaasToken({ roomName, participantName, moderator: true })
      .then((data) => {
        if (cancelled) return;
        setJwt(data.jwt);
        if (data.appId) setAppId(data.appId);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Could not load meeting token");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [roomName, participantName]);

  const commonProps = {
    roomName,
    configOverwrite: getJitsiConfigOverwrite(),
    interfaceConfigOverwrite: {
      ...getJitsiInterfaceConfig(),
    },
    userInfo: {
      displayName: participantName || "Guest",
      email: "",
    },
    onApiReady: handleApiReady,
    getIFrameRef: applyIframeStyle,
    lang: "en",
  };

  if (loading) {
    return (
      <div className="biz-shell flex items-center justify-center">
        <div className="biz-panel relative z-10 w-full max-w-sm p-8 text-center animate-fadeUp">
          <img
            src={bizLogo}
            alt=""
            className="mx-auto mb-5 h-14 w-14 rounded-2xl object-cover shadow-soft"
          />
          <p className="font-display text-xl font-semibold text-biz-ink">
            Preparing your room
          </p>
          <p className="mt-2 text-sm text-biz-mute">Securing connection…</p>
          <div className="mx-auto mt-6 h-1 w-32 overflow-hidden rounded-full bg-biz-mist">
            <div className="h-full w-1/2 animate-pulseSoft rounded-full bg-biz-yellow" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="biz-shell flex items-center justify-center px-4">
        <div className="biz-panel relative z-10 w-full max-w-md p-8 text-center animate-fadeUp">
          <p className="font-display text-xl font-semibold text-biz-ink">
            Unable to start meeting
          </p>
          <p className="mt-2 text-sm text-biz-mute">{error}</p>
          <button type="button" className="biz-btn-primary mt-6" onClick={onLeave}>
            Back to lobby
          </button>
        </div>
      </div>
    );
  }

  return (
    <MeetingChrome roomName={roomName}>
      <div className="relative min-h-0 flex-1">
        <AudioRecoveryBanner
          status={recovery.status}
          detail={recovery.detail}
          onRestore={recovery.restoreAudio}
          onRejoin={onRejoin}
        />
        <RecordingPanel
          roomName={roomName}
          displayName={participantName}
          asHost={asHost}
          externalApi={externalApi}
          logMeeting={logMeeting}
        />
        {JITSI_MODE === "jaas" ? (
          <JaaSMeeting appId={appId} jwt={jwt} {...commonProps} />
        ) : (
          <JitsiMeeting domain={JITSI_DOMAIN} {...commonProps} />
        )}
      </div>
    </MeetingChrome>
  );
}
